import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
// Screenshot via native Screen Capture API (instant, no DOM cloning)
import {
  X,
  Send,
  ArrowLeft,
  Camera,
  MousePointer2,
  Square,
  Pencil,
  Type,
  Undo2,
  Loader2,
  CheckCircle,
  AlertCircle,
  ChevronDown,
} from 'lucide-react';
import { api } from '../../api/client';
import { getCollectedErrors, getDeviceInfo } from '../../utils/errorCollector';

interface ScreenshotReporterProps {
  isOpen: boolean;
  onClose: () => void;
  defaultType?: 'bug_report' | 'feature_request';
}

type AnnotationTool = 'arrow' | 'rect' | 'freehand' | 'text';
type AnnotationColor = '#ef4444' | '#eab308' | '#3b82f6';

interface Annotation {
  tool: AnnotationTool;
  color: string;
  points: { x: number; y: number }[];
  text?: string;
}

const API_BASE = import.meta.env.VITE_API_URL || '';

export default function ScreenshotReporter({ isOpen, onClose, defaultType = 'bug_report' }: ScreenshotReporterProps) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  // Steps: 'capture' | 'annotate' | 'form' | 'success'
  const [step, setStep] = useState<'capture' | 'annotate' | 'form' | 'success'>('capture');
  const [screenshotDataUrl, setScreenshotDataUrl] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);

  // Annotation state
  const [activeTool, setActiveTool] = useState<AnnotationTool>('arrow');
  const [activeColor, setActiveColor] = useState<AnnotationColor>('#ef4444');
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentAnnotation, setCurrentAnnotation] = useState<Annotation | null>(null);

  // Text input state (replaces browser prompt())
  const [textInput, setTextInput] = useState<{ visible: boolean; position: { x: number; y: number }; value: string }>({ visible: false, position: { x: 0, y: 0 }, value: '' });

  // Form state
  const [reportType, setReportType] = useState<'bug_report' | 'feature_request'>(defaultType);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('normal');

  // Final annotated screenshot (captured when moving from annotate → form)
  const [annotatedDataUrl, setAnnotatedDataUrl] = useState<string | null>(null);

  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [resultTicketNumber, setResultTicketNumber] = useState('');

  useEffect(() => {
    if (isOpen && step === 'capture') {
      captureScreenshot();
    }
  }, [isOpen]);

  useEffect(() => {
    setReportType(defaultType);
  }, [defaultType]);

  const captureScreenshot = useCallback(async () => {
    setCapturing(true);
    setCaptureError(null);
    try {
      // Use native Screen Capture API — instant, no DOM cloning
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'browser' },
        preferCurrentTab: true,
      } as DisplayMediaStreamOptions & { preferCurrentTab?: boolean });

      const video = document.createElement('video');
      video.srcObject = stream;
      await video.play();
      // Wait for first frame to be available
      await new Promise(r => setTimeout(r, 150));

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d')!.drawImage(video, 0, 0);
      stream.getTracks().forEach(track => track.stop());

      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
      setScreenshotDataUrl(dataUrl);
      setStep('annotate');
    } catch {
      // User denied permission or API not available — skip to form
      setStep('form');
    } finally {
      setCapturing(false);
    }
  }, []);

  // Draw annotations on canvas
  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;

    ctx.drawImage(img, 0, 0);

    const allAnnotations = currentAnnotation
      ? [...annotations, currentAnnotation]
      : annotations;

    for (const ann of allAnnotations) {
      ctx.strokeStyle = ann.color;
      ctx.fillStyle = ann.color;
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      if (ann.tool === 'arrow' && ann.points.length >= 2) {
        const start = ann.points[0];
        const end = ann.points[ann.points.length - 1];
        // Line
        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(end.x, end.y);
        ctx.stroke();
        // Arrowhead
        const angle = Math.atan2(end.y - start.y, end.x - start.x);
        const headLen = 15;
        ctx.beginPath();
        ctx.moveTo(end.x, end.y);
        ctx.lineTo(end.x - headLen * Math.cos(angle - Math.PI / 6), end.y - headLen * Math.sin(angle - Math.PI / 6));
        ctx.moveTo(end.x, end.y);
        ctx.lineTo(end.x - headLen * Math.cos(angle + Math.PI / 6), end.y - headLen * Math.sin(angle + Math.PI / 6));
        ctx.stroke();
      } else if (ann.tool === 'rect' && ann.points.length >= 2) {
        const start = ann.points[0];
        const end = ann.points[ann.points.length - 1];
        ctx.strokeRect(
          Math.min(start.x, end.x),
          Math.min(start.y, end.y),
          Math.abs(end.x - start.x),
          Math.abs(end.y - start.y)
        );
      } else if (ann.tool === 'freehand' && ann.points.length >= 2) {
        ctx.beginPath();
        ctx.moveTo(ann.points[0].x, ann.points[0].y);
        for (let i = 1; i < ann.points.length; i++) {
          ctx.lineTo(ann.points[i].x, ann.points[i].y);
        }
        ctx.stroke();
      } else if (ann.tool === 'text' && ann.text && ann.points.length > 0) {
        ctx.font = 'bold 18px sans-serif';
        ctx.fillText(ann.text, ann.points[0].x, ann.points[0].y);
      }
    }
  }, [annotations, currentAnnotation]);

  useEffect(() => {
    if (step === 'annotate' && screenshotDataUrl) {
      const img = new Image();
      img.onload = () => {
        imageRef.current = img;
        redrawCanvas();
      };
      img.src = screenshotDataUrl;
    }
  }, [step, screenshotDataUrl]);

  useEffect(() => {
    redrawCanvas();
  }, [annotations, currentAnnotation, redrawCanvas]);

  // Convert client coordinates to canvas coordinates
  const getCoordsFromClient = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const getCanvasCoords = (e: React.MouseEvent<HTMLCanvasElement>) => {
    return getCoordsFromClient(e.clientX, e.clientY);
  };

  const startDrawing = (coords: { x: number; y: number }) => {
    if (activeTool === 'text') {
      setTextInput({ visible: true, position: coords, value: '' });
      return;
    }
    setIsDrawing(true);
    setCurrentAnnotation({ tool: activeTool, color: activeColor, points: [coords] });
  };

  const moveDrawing = (coords: { x: number; y: number }) => {
    if (!isDrawing || !currentAnnotation) return;
    if (activeTool === 'freehand') {
      setCurrentAnnotation((prev) => prev ? { ...prev, points: [...prev.points, coords] } : null);
    } else {
      setCurrentAnnotation((prev) => prev ? { ...prev, points: [prev.points[0], coords] } : null);
    }
  };

  const stopDrawing = () => {
    if (currentAnnotation && currentAnnotation.points.length >= 2) {
      setAnnotations((prev) => [...prev, currentAnnotation]);
    }
    setCurrentAnnotation(null);
    setIsDrawing(false);
  };

  const handleCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => startDrawing(getCanvasCoords(e));
  const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => moveDrawing(getCanvasCoords(e));
  const handleCanvasMouseUp = () => stopDrawing();

  // Touch event handlers for mobile
  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const touch = e.touches[0];
    startDrawing(getCoordsFromClient(touch.clientX, touch.clientY));
  };
  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (!isDrawing || !currentAnnotation) return;
    const touch = e.touches[0];
    moveDrawing(getCoordsFromClient(touch.clientX, touch.clientY));
  };
  const handleTouchEnd = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    stopDrawing();
  };

  const handleTextInputSubmit = () => {
    if (textInput.value.trim()) {
      setAnnotations((prev) => [...prev, { tool: 'text', color: activeColor, points: [textInput.position], text: textInput.value }]);
    }
    setTextInput({ visible: false, position: { x: 0, y: 0 }, value: '' });
  };

  const handleUndo = () => {
    setAnnotations((prev) => prev.slice(0, -1));
  };

  const getAnnotatedScreenshot = (): string | null => {
    const canvas = canvasRef.current;
    if (!canvas) return screenshotDataUrl;
    return canvas.toDataURL('image/png');
  };

  const handleSubmit = async () => {
    if (!subject.trim() || !description.trim()) return;
    setSubmitting(true);

    try {
      const finalScreenshot = annotatedDataUrl || screenshotDataUrl;
      const deviceInfo = getDeviceInfo();
      const errors = getCollectedErrors();

      // Prepend current page URL so the reader knows where the report originated
      const pageUrl = window.location.href;
      const fullMessage = `📍 Seite: ${pageUrl}\n\n${description}`;

      const response = await api.post('/support/tickets', {
        subject,
        category: reportType,
        priority,
        message: fullMessage,
        source: 'wawi',
        deviceInfo: {
          ...deviceInfo,
          ...errors,
        },
      });

      if (response?.success) {
        const ticketId = response.ticketId;
        const messageId = response.messageId;
        setResultTicketNumber(response.ticketNumber);

        // Upload screenshot as attachment linked to the first message
        if (finalScreenshot && ticketId) {
          try {
            const blob = await (await fetch(finalScreenshot)).blob();
            const formData = new FormData();
            formData.append('file', blob, `screenshot-${Date.now()}.png`);
            if (messageId) {
              formData.append('messageId', messageId);
            }

            const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
            await fetch(`${API_BASE}/support/tickets/${ticketId}/attachments`, {
              method: 'POST',
              headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              body: formData,
            });
          } catch (uploadErr) {
            console.error('Screenshot upload failed:', uploadErr);
          }
        }

        setStep('success');
      } else {
        setCaptureError(response?.error || t('common.error'));
      }
    } catch (err) {
      setCaptureError(t('common.error'));
      console.error('Bug report submission failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep('capture');
    setScreenshotDataUrl(null);
    setAnnotatedDataUrl(null);
    setAnnotations([]);
    setCurrentAnnotation(null);
    setSubject('');
    setDescription('');
    setPriority('normal');
    setCaptureError(null);
    setResultTicketNumber('');
    onClose();
  };

  if (!isOpen) return null;

  // Capture step: no overlay — getDisplayMedia needs the page visible
  if (step === 'capture') {
    return null;
  }

  // Annotation step
  if (step === 'annotate') {
    const tools: { id: AnnotationTool; icon: React.ReactNode; label: string }[] = [
      { id: 'arrow', icon: <MousePointer2 className="w-4 h-4" />, label: t('bugReporter.tools.arrow') },
      { id: 'rect', icon: <Square className="w-4 h-4" />, label: t('bugReporter.tools.rect') },
      { id: 'freehand', icon: <Pencil className="w-4 h-4" />, label: t('bugReporter.tools.freehand') },
      { id: 'text', icon: <Type className="w-4 h-4" />, label: t('bugReporter.tools.text') },
    ];

    const colors: { value: AnnotationColor; label: string }[] = [
      { value: '#ef4444', label: t('bugReporter.colors.red') },
      { value: '#eab308', label: t('bugReporter.colors.yellow') },
      { value: '#3b82f6', label: t('bugReporter.colors.blue') },
    ];

    return (
      <div id="screenshot-reporter-overlay" className="fixed inset-0 z-[60] bg-gray-900 flex flex-col">
        {/* Toolbar - responsive: wraps on mobile */}
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-gray-800 border-b border-gray-700">
          <div className="flex items-center gap-1.5">
            {tools.map((tool) => (
              <button
                key={tool.id}
                onClick={() => setActiveTool(tool.id)}
                className={`p-2 rounded-lg transition-colors ${
                  activeTool === tool.id
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-300 hover:bg-gray-700'
                }`}
                title={tool.label}
              >
                {tool.icon}
              </button>
            ))}
            <div className="w-px h-6 bg-gray-600 mx-0.5" />
            {colors.map((color) => (
              <button
                key={color.value}
                onClick={() => setActiveColor(color.value)}
                className={`w-6 h-6 rounded-full border-2 transition-all ${
                  activeColor === color.value ? 'border-white scale-110' : 'border-gray-600'
                }`}
                style={{ backgroundColor: color.value }}
                title={color.label}
              />
            ))}
            <div className="w-px h-6 bg-gray-600 mx-0.5" />
            <button
              onClick={handleUndo}
              disabled={annotations.length === 0}
              className="p-2 rounded-lg text-gray-300 hover:bg-gray-700 disabled:opacity-30"
              title={t('bugReporter.undo')}
            >
              <Undo2 className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => {
                setAnnotations([]);
                setScreenshotDataUrl(null);
                setStep('capture');
                setTimeout(captureScreenshot, 100);
              }}
              className="p-2 sm:px-3 sm:py-1.5 text-sm text-gray-300 hover:bg-gray-700 rounded-lg"
              title={t('bugReporter.newScreenshot')}
            >
              <Camera className="w-4 h-4 sm:inline sm:mr-1" />
              <span className="hidden sm:inline">{t('bugReporter.newScreenshot')}</span>
            </button>
            <button onClick={handleClose} className="px-3 py-1.5 text-sm text-gray-300 hover:bg-gray-700 rounded-lg">
              {t('common.cancel')}
            </button>
            <button
              onClick={() => {
                const canvas = canvasRef.current;
                if (canvas) setAnnotatedDataUrl(canvas.toDataURL('image/png'));
                setStep('form');
              }}
              className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              {t('bugReporter.next')}
            </button>
          </div>
        </div>
        {/* Canvas */}
        <div className="flex-1 overflow-auto flex items-center justify-center p-2 sm:p-4 relative">
          <canvas
            ref={canvasRef}
            onMouseDown={handleCanvasMouseDown}
            onMouseMove={handleCanvasMouseMove}
            onMouseUp={handleCanvasMouseUp}
            onMouseLeave={handleCanvasMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            className="max-w-full max-h-full cursor-crosshair shadow-2xl"
            style={{ imageRendering: 'auto', touchAction: 'none' }}
          />
          {/* Inline text input overlay (replaces prompt()) */}
          {textInput.visible && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 z-10">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-xl mx-4 w-full max-w-sm">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('bugReporter.enterText')}
                </label>
                <input
                  type="text"
                  value={textInput.value}
                  onChange={(e) => setTextInput((prev) => ({ ...prev, value: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleTextInputSubmit(); if (e.key === 'Escape') setTextInput({ visible: false, position: { x: 0, y: 0 }, value: '' }); }}
                  className="input w-full mb-3"
                  autoFocus
                />
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setTextInput({ visible: false, position: { x: 0, y: 0 }, value: '' })} className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                    {t('common.cancel')}
                  </button>
                  <button onClick={handleTextInputSubmit} className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                    {t('common.add')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Form step
  if (step === 'form') {
    return (
      <div id="screenshot-reporter-overlay" className="fixed inset-0 z-[60] flex justify-end">
        <div className="absolute inset-0 bg-black/30" onClick={handleClose} />
        <div className="relative w-full max-w-lg bg-white dark:bg-gray-800 shadow-xl flex flex-col animate-slide-in-right">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              {screenshotDataUrl && (
                <button onClick={() => setStep('annotate')} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {reportType === 'bug_report' ? t('bugReporter.reportBug') : t('bugReporter.suggestFeature')}
              </h2>
            </div>
            <button onClick={handleClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Error */}
          {captureError && (
            <div className="mx-4 mt-3 px-3 py-2 rounded-lg text-sm bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{captureError}</span>
              <button onClick={() => setCaptureError(null)} className="ml-auto"><X className="w-3.5 h-3.5" /></button>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Screenshot preview */}
            {(annotatedDataUrl || screenshotDataUrl) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('bugReporter.screenshot')}
                </label>
                <div
                  onClick={() => setStep('annotate')}
                  className="relative cursor-pointer rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 hover:border-primary-400 transition-colors"
                >
                  <img
                    src={annotatedDataUrl || screenshotDataUrl || ''}
                    alt="Screenshot"
                    className="w-full h-auto max-h-40 object-cover"
                  />
                  <div className="absolute inset-0 bg-black/0 hover:bg-black/10 flex items-center justify-center transition-colors">
                    <span className="text-xs bg-black/50 text-white px-2 py-1 rounded opacity-0 hover:opacity-100 transition-opacity">
                      {t('bugReporter.editScreenshot')}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('bugReporter.type')} *
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setReportType('bug_report')}
                  className={`p-3 rounded-lg border text-sm font-medium text-left transition-colors ${
                    reportType === 'bug_report'
                      ? 'border-red-400 bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300 dark:border-red-600'
                      : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  🐛 {t('bugReporter.bugReport')}
                </button>
                <button
                  onClick={() => setReportType('feature_request')}
                  className={`p-3 rounded-lg border text-sm font-medium text-left transition-colors ${
                    reportType === 'feature_request'
                      ? 'border-yellow-400 bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300 dark:border-yellow-600'
                      : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  💡 {t('bugReporter.featureRequest')}
                </button>
              </div>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('bugReporter.subject')} *
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={reportType === 'bug_report' ? t('bugReporter.subjectBugPlaceholder') : t('bugReporter.subjectFeaturePlaceholder')}
                className="input w-full"
                autoFocus
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('bugReporter.description')} *
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={reportType === 'bug_report' ? t('bugReporter.descriptionBugPlaceholder') : t('bugReporter.descriptionFeaturePlaceholder')}
                className="input w-full"
                rows={5}
              />
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('bugReporter.priority')}
              </label>
              <select value={priority} onChange={(e) => setPriority(e.target.value)} className="input w-full">
                <option value="low">{t('admin.support.priority.low')}</option>
                <option value="normal">{t('admin.support.priority.normal')}</option>
                <option value="high">{t('admin.support.priority.high')}</option>
                <option value="urgent">{t('admin.support.priority.urgent')}</option>
              </select>
            </div>

          </div>

          {/* Submit */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleSubmit}
              disabled={!subject.trim() || !description.trim() || submitting}
              className="btn btn-primary w-full"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {t('bugReporter.submit')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Success step
  if (step === 'success') {
    return (
      <div id="screenshot-reporter-overlay" className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center max-w-sm mx-4 shadow-2xl">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {t('bugReporter.successTitle')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-1">
            {t('bugReporter.successMessage', { number: resultTicketNumber })}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mb-6">
            {t('bugReporter.successThank')}
          </p>
          <button onClick={handleClose} className="btn btn-primary">
            {t('common.close')}
          </button>
        </div>
      </div>
    );
  }

  return null;
}
